import type { ConfigurationPackage } from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useState } from 'react'

import { DraftIndicator } from '../../../components/draftIndicator/DraftIndicator'
import { HoverEditButton } from '../../../components/hoverEditGroup/HoverEditButton'
import { OperationApproval } from '../../../components/operationApproval/OperationApproval'
import { useParameterContext } from '../../../context/parameterContext'
import { useConfigurationComponentAvailability } from '../../../hooks/useConfigurationComponentAvailability'
import { useIsInlineEditingAvailable } from '../../../hooks/useIsInlineEditingAvailable'
import { useParameterDefinitionId } from '../../../hooks/useParameterDefinitionId'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { UploadValuesCsvFileToWorkspaceModal } from './UploadValuesCsvFileToWorkspaceModal'
import { useDownloadValues } from './useDownloadValues'

const isDraftOperation = ({ isDraft }: { isDraft: boolean }) => isDraft

export const ParameterValuesHeader = () => {
  const translate = useTranslate()
  const {
    parameterValueEntityOperationsForView,
    amendOperationsInView,
    parameterDefinitionOperation,
    reviewingOperation,
  } = useParameterContext()

  const {
    workspaceKey,
    onAddOrUpdateOperation,
    onRemoveOperation,
    parameterValueEntityOperations,
  } = useOperationsContext()
  const [isUploadCsvModalOpen, setIsUploadCsvModalOpen] = useState(false)
  const [isUploadingOperations, setIsUploadingOperations] = useState(false)
  const parameterDefinitionId = useParameterDefinitionId()
  const isInlineEditingAllowed = useIsInlineEditingAvailable()
  const isConfigurationComponentAvailable =
    useConfigurationComponentAvailability()

  const areValuesDraftInCurrentView =
    !!workspaceKey &&
    (!!parameterValueEntityOperationsForView?.some(isDraftOperation) ||
      amendOperationsInView.some(isDraftOperation))

  const { mutateAsync: downloadValuesAsCsv, isPending: isDownloadingValues } =
    useDownloadValues()
  const isExtrinsic =
    !!parameterDefinitionOperation?.internalOperation.entity?.isExtrinsic

  const handleUploadValueFromConfigurationPackage = async ({
    configurationPackage,
    layer: destinationLayer,
    block: destinationBlock,
  }: {
    configurationPackage: Optional<ConfigurationPackage>
    layer: LayerKey
    block: string
  }) => {
    setIsUploadCsvModalOpen(false)
    setIsUploadingOperations(true)

    const parameterValueOperationsOnLayer = parameterValueEntityOperations
      .get(destinationBlock)
      ?.get(destinationLayer)

    const currentValueOperations = parameterValueOperationsOnLayer?.get(
      parameterDefinitionId ?? '',
    )

    // The upload replaces the parameter's draft values on the layer, amendments included:
    // the uploaded values are the layer's new draft state, so an amend draft left behind
    // would apply its deltas on top of values it was never authored against.
    const currentDraftParameterValueOperationsOnLayer = (
      currentValueOperations ?? []
    ).filter(
      (valueOperation) =>
        isDraftOperation(valueOperation) &&
        valueOperation.layer.equals(destinationLayer),
    )

    await Promise.all(
      currentDraftParameterValueOperationsOnLayer.map((currentDraftOperation) =>
        onRemoveOperation(currentDraftOperation.internalOperation),
      ),
    )

    await Promise.all(
      (configurationPackage?.changesets ?? [])
        .filter(notEmpty)
        .flatMap((changeSet) =>
          (changeSet.operations ?? []).filter(notEmpty).map(
            async (changeSetOperation) =>
              await onAddOrUpdateOperation({
                operation: changeSetOperation,
                layer: destinationLayer,
                block: destinationBlock,
                shouldOpenUnifiedBranchWorkspace: true,
              }),
          ),
        ),
    )

    setIsUploadingOperations(false)
  }

  return (
    <>
      <div className="relative">
        <h4
          className={cx(
            'absolute left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-row items-center gap-x-2 rounded-md p-2 uppercase',
            paperStyles({ isFaded: true }),
          )}
        >
          <span className="text-sm">
            {translate('parameterViewer.values.title')}
          </span>
          {areValuesDraftInCurrentView && <DraftIndicator />}
          {isConfigurationComponentAvailable && (
            <HoverEditButton
              icon={
                isDownloadingValues ? (
                  <CircularSpinner className="size-4" />
                ) : (
                  <Icon name="ArrowDownTray" />
                )
              }
              onClick={isDownloadingValues ? () => {} : downloadValuesAsCsv}
            />
          )}
          {isInlineEditingAllowed && !isExtrinsic && (
            <HoverEditButton
              icon={
                isUploadingOperations ? (
                  <CircularSpinner className="size-4" />
                ) : (
                  <Icon name="ArrowUpTray" />
                )
              }
              onClick={() => setIsUploadCsvModalOpen(true)}
            />
          )}
          {!!reviewingOperation && (
            <OperationApproval
              changesetKey={reviewingOperation.changesetHistory?.changesetKey}
              operationKey={reviewingOperation.internalOperation.operationKey}
              actions={reviewingOperation.actions}
            />
          )}
        </h4>
      </div>
      {isUploadCsvModalOpen && (
        <UploadValuesCsvFileToWorkspaceModal
          onUpload={handleUploadValueFromConfigurationPackage}
          onClose={() => setIsUploadCsvModalOpen(false)}
        />
      )}
    </>
  )
}
